import { useState } from 'react';
import { Bell, X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRealtimeJobUpdates, type JobUpdate } from '../hooks/useRealtimeJobUpdates';

interface NotificationSystemProps {
  userId: string;
  onNotificationClick?: (notification: JobUpdate) => void;
}

const NotificationSystem = ({ userId, onNotificationClick }: NotificationSystemProps) => {
  const [notifications, setNotifications] = useState<JobUpdate[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleNewUpdate = (update: JobUpdate) => {
    setNotifications(prev => [update, ...prev.slice(0, 9)]); // Keep last 10
    setUnreadCount(prev => prev + 1);
  };

  useRealtimeJobUpdates({ userId, onUpdate: handleNewUpdate });

  const handleNotificationClick = (notification: JobUpdate) => {
    onNotificationClick?.(notification);
    setShowNotifications(false);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const getNotificationIcon = (type: JobUpdate['type']) => {
    switch (type) {
      case 'application':
        return <Info className="text-blue-400" size={16} />;
      case 'status_change':
        return <AlertCircle className="text-yellow-400" size={16} />;
      case 'assignment':
        return <CheckCircle className="text-green-400" size={16} />;
      case 'completion':
        return <CheckCircle className="text-green-400" size={16} />;
      default:
        return <Bell className="text-gray-400" size={16} />;
    }
  };

  const formatTime = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setShowNotifications(!showNotifications);
          if (showNotifications) setUnreadCount(0);
        }}
        className="relative p-2 text-foreground hover:bg-secondary"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-hidden">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <h3 className="font-semibold text-card-foreground">Notifications</h3>
            <div className="flex items-center gap-2">
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearNotifications}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNotifications(false)}
                className="p-1"
              >
                <X size={16} />
              </Button>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Bell className="mx-auto mb-2 opacity-50" size={32} />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className="p-3 border-b border-border hover:bg-muted cursor-pointer transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {getNotificationIcon(notification.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-card-foreground truncate">
                        {notification.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatTime(notification.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSystem;